import { motion } from "framer-motion";
import { HelpCircle } from "lucide-react";
import { SPEAKERS } from "@/lib/programData";
import SILHOUETTE from "@/lib/speakerPlaceholder";

function Card({ s, i }) {
  return (
    <motion.a
      href={`#program-${s.slug}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.7, delay: (i % 4) * 0.05, ease: [0.2, 0.8, 0.2, 1] }}
      id={`speaker-${s.slug}`}
      data-testid={`speaker-${s.slug}`}
      className="group block"
    >
      <div className="spotlight frame-shadow overflow-hidden aspect-[4/5] bg-[#0F2647]">
        <img
          src={s.photo || SILHOUETTE}
          alt={s.name}
          loading="lazy"
          className="w-full h-full object-cover"
          onError={(e) => {
            e.currentTarget.src = SILHOUETTE;
          }}
        />
      </div>
      <div className="mt-4">
        <div className="font-display uppercase text-lg sm:text-xl text-[#F5EBD9] leading-tight group-hover:text-[#F5892E] transition-colors">
          {s.name}
        </div>
        <div className="mt-2 text-sm text-[#F5EBD9]/70 leading-snug">
          {s.role}
        </div>
      </div>
    </motion.a>
  );
}

export default function Speakers() {
  return (
    <section
      id="speakers"
      data-testid="speakers-section"
      className="relative bg-[#0B1F3D] text-[#F5EBD9] py-24 sm:py-32 lg:py-40 overflow-hidden"
    >
      <div className="absolute inset-0 sunset-glow opacity-30 pointer-events-none" />
      <div className="max-w-[1440px] mx-auto px-6 sm:px-10 lg:px-16 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9 }}
          className="mb-14 lg:mb-20"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#F5892E]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.32em] text-[#F5EBD9]/70">
              Сцената — Лектори · Издание 2026
            </span>
          </div>
          <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[1.02] max-w-[24ch]">
            Един ден. Една сцена.{" "}
            <span className="text-[#F5892E]">Най-силните имена</span> от българския
            имотен пазар.
          </h2>
          <p className="mt-6 text-lg text-[#F5EBD9]/75 max-w-[64ch] leading-relaxed">
            Инвеститори, предприемачи, банкери, анализатори, строители и професионалисти —
            хората, които реално движат пазара на имоти в България.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-10">
          {SPEAKERS.map((s, i) => (
            <Card key={s.slug} s={s} i={i} />
          ))}

          {/* VIP mystery guest card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            data-testid="speaker-vip-mystery"
            className="relative"
          >
            <div className="relative frame-shadow overflow-hidden aspect-[4/5] bg-gradient-to-br from-[#12294F] via-[#0B1F3D] to-[#050d1e] border border-[#E0B95C]/40 flex items-center justify-center">
              <div className="absolute inset-0 pointer-events-none" style={{
                background: "radial-gradient(ellipse at center, rgba(224,185,92,0.18), transparent 65%)"
              }} />
              <HelpCircle className="w-24 h-24 text-[#E0B95C]/80 relative z-10" strokeWidth={1.2} />
              <div className="absolute top-4 left-4 bg-[#E0B95C] text-[#0B1F3D] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.32em]">
                VIP Only
              </div>
              <div className="absolute bottom-4 left-4 right-4 text-center">
                <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#E0B95C]">
                  Гост изненада
                </div>
                <div className="mt-1 font-display uppercase text-sm text-[#F5EBD9]/80">
                  Разкрива се на събитието
                </div>
              </div>
            </div>
            <div className="mt-4">
              <div className="font-display uppercase text-lg sm:text-xl text-[#E0B95C] leading-tight">
                VIP гост
              </div>
              <div className="mt-2 text-sm text-[#F5EBD9]/70 leading-snug">
                Limited access · 19:00 – 20:00
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
